import React, { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient, { API_ENDPOINTS, BASE_URL } from '../../config/api'
import { isSmallShopMode } from '../../utils/smallShopMode'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2
})

const resolveAttachmentUrl = (path) => {
  if (!path) {
    return null
  }
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }
  if (path.startsWith('/')) {
    return `${BASE_URL}${path}`
  }
  return `${BASE_URL}/${path}`
}

const extractAttachmentName = (path) => {
  if (!path) {
    return ''
  }
  const segments = path.split('/')
  return segments[segments.length - 1] || 'attachment'
}

const ManageExpenses = () => {
  const smallShopMode = isSmallShopMode()
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')

  const [categories, setCategories] = useState([])
  const [branches, setBranches] = useState([])

  const [filters, setFilters] = useState({
    branch: '',
    category: '',
    startDate: '',
    endDate: ''
  })

  const [refreshing, setRefreshing] = useState(false)

  const fetchLookups = useCallback(async () => {
    try {
      const [categoryResponse, branchResponse] = await Promise.all([
        apiClient.get(API_ENDPOINTS.EXPENSE_CATEGORIES),
        apiClient.get(API_ENDPOINTS.BRANCHES)
      ])
      const normalize = (response) => (Array.isArray(response.data) ? response.data : response.data?.results || [])
      setCategories(normalize(categoryResponse))
      setBranches(normalize(branchResponse))
    } catch (error) {
      console.error('Failed to load filters for expenses:', error)
    }
  }, [])

  const fetchExpenses = useCallback(async () => {
    try {
      setErrorMessage('')
      setLoading(true)
      const params = {}
      if (filters.branch) {
        params.branch_id = filters.branch
      }
      if (filters.category) {
        params.category_id = filters.category
      }
      if (filters.startDate) {
        params.start_date = filters.startDate
      }
      if (filters.endDate) {
        params.end_date = filters.endDate
      }

      const response = await apiClient.get(API_ENDPOINTS.EXPENSES, { params })
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []
      setExpenses(data)
    } catch (error) {
      console.error('Failed to load expenses:', error)
      setErrorMessage(
        error.response?.data?.detail ||
          'Unable to load expenses right now. Please adjust your filters or try again later.'
      )
      setExpenses([])
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [filters])

  useEffect(() => {
    fetchLookups()
  }, [fetchLookups])

  useEffect(() => {
    fetchExpenses()
  }, [fetchExpenses])

  const handleFilterChange = (event) => {
    const { name, value } = event.target
    setFilters((previous) => ({
      ...previous,
      [name]: value
    }))
  }

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense? This cannot be undone.')) {
      return
    }

    try {
      await apiClient.delete(`${API_ENDPOINTS.EXPENSES}${expenseId}/`)
      setExpenses((previous) => previous.filter((expense) => expense.id !== expenseId))
    } catch (error) {
      console.error('Failed to delete expense:', error)
      setErrorMessage(error.response?.data?.detail || 'Unable to delete expense. Please try again.')
    }
  }

  const totalAmount = useMemo(() => {
    return expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0)
  }, [expenses])

  const handleRefresh = () => {
    setRefreshing(true)
    fetchExpenses()
  }

  const renderTable = () => {
    if (loading) {
      return (
        <div className="py-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )
    }

    if (!expenses.length) {
      return (
        <div className="py-5 text-center text-muted">
          <i className="bi bi-file-earmark-break fs-1 d-block mb-3" />
          <p className="mb-0">No expenses match your current filters.</p>
        </div>
      )
    }

    return (
      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th scope="col">Description</th>
              <th scope="col">Category</th>
              <th scope="col">Branch</th>
              {!smallShopMode && <th scope="col">Warehouse</th>}
              <th scope="col" className="text-end">Amount</th>
              <th scope="col" className="text-end">Date</th>
              <th scope="col" className="text-end">Attachment</th>
              <th scope="col" className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((expense) => (
              <tr key={expense.id}>
                <td>
                  <div className="fw-semibold text-dark">{expense.description || 'No description'}</div>
                  <small className="text-muted">Recorded by {expense.created_by_email || 'Unknown'}</small>
                </td>
                <td>{expense.category_name || '—'}</td>
                <td>{expense.branch_name || '—'}</td>
                {!smallShopMode && <td>{expense.warehouse_name || '—'}</td>}
                <td className="text-end fw-semibold">{currencyFormatter.format(Number(expense.amount) || 0)}</td>
                <td className="text-end text-muted">
                  {expense.incurred_on ? new Date(expense.incurred_on).toLocaleDateString() : '—'}
                </td>
                <td className="text-end">
                  {expense.attachment ? (
                    <a
                      className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-2"
                      href={resolveAttachmentUrl(expense.attachment)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="bi bi-paperclip" />
                      <span className="text-truncate" style={{ maxWidth: 140 }}>
                        {extractAttachmentName(expense.attachment)}
                      </span>
                    </a>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="text-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDelete(expense.id)}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Expenses</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#">Home</a></li>
            <li className="breadcrumb-item">Expenses</li>
            <li className="breadcrumb-item active">Manage Expenses</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <label className="form-label fw-semibold">Branch</label>
            <select
              className="form-select"
              name="branch"
              value={filters.branch}
              onChange={handleFilterChange}
            >
              <option value="">All branches</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label fw-semibold">Category</label>
            <select
              className="form-select"
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
            >
              <option value="">All categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label fw-semibold">Start Date</label>
            <input
              type="date"
              className="form-control"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-semibold">End Date</label>
            <input
              type="date"
              className="form-control"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <h5 className="mb-0">Expense Ledger</h5>
            <small className="text-muted">
              {expenses.length} record{expenses.length === 1 ? '' : 's'} · {currencyFormatter.format(totalAmount)} total
            </small>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setFilters({ branch: '', category: '', startDate: '', endDate: '' })}
              disabled={loading}
            >
              Clear filters
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              {refreshing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Refreshing
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-repeat me-1" />
                  Refresh
                </>
              )}
            </button>
          </div>
        </div>

        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="card">
          <div className="card-body">
            {renderTable()}
          </div>
        </div>
      </section>
    </>
  )
}

export default ManageExpenses
